import {useCallback, useReducer} from 'react';

import {EventTriggerConfig, FileMessageBlock, TimeTriggerConfig, TriggerType} from './RoutineTypes';

export interface RoutineFormState {
  name: string;
  description: string;
  prompt: string;
  fileBlocks: Array<FileMessageBlock>;
  triggerType: TriggerType | null;
  triggerConfig: Partial<TimeTriggerConfig> | Partial<EventTriggerConfig>;
}

interface ReducerState {
  form: RoutineFormState;
  baseline: RoutineFormState;
}

type RoutineFormAction =
  | {type: 'SET_NAME'; value: string}
  | {type: 'SET_DESCRIPTION'; value: string}
  | {type: 'SET_PROMPT'; value: string}
  | {type: 'REMOVE_FILE_BLOCK'; index: number}
  | {type: 'SET_TRIGGER_TYPE'; value: TriggerType}
  | {type: 'UPDATE_TRIGGER_CONFIG'; updates: Partial<TimeTriggerConfig | EventTriggerConfig>}
  | {type: 'RESET'; state: RoutineFormState};

function formReducer(state: ReducerState, action: RoutineFormAction): ReducerState {
  switch (action.type) {
    case 'SET_NAME':
      return {...state, form: {...state.form, name: action.value}};
    case 'SET_DESCRIPTION':
      return {...state, form: {...state.form, description: action.value}};
    case 'SET_PROMPT':
      return {...state, form: {...state.form, prompt: action.value}};
    case 'REMOVE_FILE_BLOCK':
      return {
        ...state,
        form: {...state.form, fileBlocks: state.form.fileBlocks.filter((_, i) => i !== action.index)},
      };
    case 'SET_TRIGGER_TYPE':
      if (action.value === state.form.triggerType) {
        return state;
      }
      return {
        ...state,
        form: {
          ...state.form,
          triggerType: action.value,
          triggerConfig:
            action.value === TriggerType.TIME_BASED
              ? getDefaultTimeTriggerConfig()
              : getDefaultEventTriggerConfig(),
        },
      };
    case 'UPDATE_TRIGGER_CONFIG':
      return {
        ...state,
        form: {...state.form, triggerConfig: {...state.form.triggerConfig, ...action.updates}},
      };
    case 'RESET':
      return {form: action.state, baseline: action.state};
    default:
      return state;
  }
}

export function getDefaultTimeTriggerConfig(): Partial<TimeTriggerConfig> {
  return {
    timezone: new Intl.DateTimeFormat().resolvedOptions().timeZone,
    freq_type: 'weekly',
    recurrence_interval: 1,
    weekly_days: [],
  };
}

export function getDefaultEventTriggerConfig(): Partial<EventTriggerConfig> {
  return {
    object_type: '',
    field: '',
    object_ids: [],
    to_values: [],
  };
}

// TODO(nweeden): Consider replacing reducer + shallowFormEqual with immer + react-fast-compare
function shallowFormEqual(a: RoutineFormState, b: RoutineFormState): boolean {
  return (
    a.name === b.name &&
    a.description === b.description &&
    a.prompt === b.prompt &&
    a.triggerType === b.triggerType &&
    JSON.stringify(a.triggerConfig) === JSON.stringify(b.triggerConfig) &&
    JSON.stringify(a.fileBlocks) === JSON.stringify(b.fileBlocks)
  );
}

interface UseRoutineFormResult {
  formState: RoutineFormState;
  dispatch: React.Dispatch<RoutineFormAction>;
  isDirty: boolean;
  reset: (newState?: RoutineFormState) => void;
}

export default function useRoutineForm(initialState: RoutineFormState): UseRoutineFormResult {
  const [state, dispatch] = useReducer(formReducer, {form: initialState, baseline: initialState});

  const isDirty = !shallowFormEqual(state.form, state.baseline);

  const reset = useCallback(
    (newState?: RoutineFormState) => {
      dispatch({type: 'RESET', state: newState ?? initialState});
    },
    [initialState]
  );

  return {formState: state.form, dispatch, isDirty, reset};
}
